/**
 * 처방 마감일 설정 화면용 순수 계산.
 * 서버 목록은 설정한 달만 주므로, 화면에서는 1~12월을 채우고 미설정 달은 기본 10일로 보여준다.
 */
import type { PrescriptionRegistrationDeadline } from '@/entities/prescription/api/prescription-registration-deadline.api';
import { format } from 'date-fns';

/** 마감일을 설정하지 않은 달에 적용되는 서버 기본값 */
export const DEFAULT_PRESCRIPTION_DEADLINE_DAY = 10;

export type DeadlineMonthStatus = 'PASSED' | 'CURRENT' | 'UPCOMING';

export interface DeadlineMonthRow {
  /** yyyyMM */
  yearMonth: string;
  /** 적용 마감일 yyyy-MM-dd (설정값, 없으면 10일) */
  deadlineDate: string;
  /** 설정 없이 기본 10일이 적용 중 */
  isDefault: boolean;
  updatedAt: string | null;
  status: DeadlineMonthStatus;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

const pad2 = (value: number) => String(value).padStart(2, '0');

export const toTodayValue = (now: Date = new Date()) =>
  format(now, 'yyyy-MM-dd');

export const defaultDeadlineDateOf = (yearMonth: string) =>
  `${yearMonth.slice(0, 4)}-${yearMonth.slice(4, 6)}-${pad2(DEFAULT_PRESCRIPTION_DEADLINE_DAY)}`;

/**
 * 연도 1~12월 행을 만든다.
 *
 * - 설정된 달: 서버 editable/deletable 플래그를 그대로 쓴다.
 * - 미설정 달: 기본 10일이 오늘 이전이면 이미 마감된 달이라 추가할 수 없다
 *   (허용하면 11일 이후 다음 달로 저장된 처방과 어긋난다 — 서버도 409로 거절).
 * - "진행 중"은 오늘 등록하면 처방등록년월이 되는 달 = 직전 달 마감은 지났고 이 달 마감은 남은 달.
 */
export const buildDeadlineMonthRows = (
  year: number,
  items: PrescriptionRegistrationDeadline[],
  today: string,
): DeadlineMonthRow[] => {
  const byYearMonth = new Map(items.map((item) => [item.yearMonth, item]));
  // 전년 12월 설정값은 이 목록에 없어 기본 10일로 가정한다 (1월 "진행 중" 표시에만 영향)
  let previousDeadline = `${year - 1}-12-${pad2(DEFAULT_PRESCRIPTION_DEADLINE_DAY)}`;

  return Array.from({ length: 12 }, (_, index) => {
    const yearMonth = `${year}${pad2(index + 1)}`;
    const configured = byYearMonth.get(yearMonth);
    const deadlineDate =
      configured?.deadlineDate ?? defaultDeadlineDateOf(yearMonth);

    let status: DeadlineMonthStatus = 'UPCOMING';
    if (deadlineDate < today) {
      status = 'PASSED';
    } else if (previousDeadline < today) {
      status = 'CURRENT';
    }
    previousDeadline = deadlineDate;

    return {
      yearMonth,
      deadlineDate,
      isDefault: !configured,
      updatedAt: configured?.updatedAt ?? null,
      status,
      canCreate: !configured && deadlineDate >= today,
      canUpdate: Boolean(configured?.editable),
      canDelete: Boolean(configured?.deletable),
    };
  });
};

/** 다이얼로그 달력에서 고를 수 있는 범위 — 해당 월 안 && 오늘 이후 */
export const getSelectableDeadlineRange = (
  yearMonth: string,
  today: string,
): { from: string; to: string } => {
  const year = Number(yearMonth.slice(0, 4));
  const month = Number(yearMonth.slice(4, 6));
  const lastDay = new Date(year, month, 0).getDate();
  const firstDate = `${yearMonth.slice(0, 4)}-${pad2(month)}-01`;
  const lastDate = `${yearMonth.slice(0, 4)}-${pad2(month)}-${pad2(lastDay)}`;
  return { from: firstDate > today ? firstDate : today, to: lastDate };
};

/**
 * 연도 선택지 (최신순). 미리 설정 기간 제한이 없어 올해 기준 앞뒤로 넉넉히 둔다.
 */
export const buildDeadlineYearOptions = (currentYear: number) =>
  Array.from({ length: 5 }, (_, index) => currentYear + 2 - index);

export const formatYearMonthLabel = (yearMonth: string) =>
  `${yearMonth.slice(0, 4)}년 ${Number(yearMonth.slice(4, 6))}월`;
